import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { api } from '@/services/api';
import { usePageTitle } from '@/shared/usePageTitle';
import { useToast } from '@/shared/toast';
import { useConfirm } from '@/shared/confirm';
import { useAuth } from '@/shared/AuthContext';
import { HydrantSchedaModal } from './HydrantSchedaModal';
import type { Hydrant } from './types';
import { downloadXlsx } from '@/shared/exports';

delete (L.Icon.Default.prototype as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const HYDRANT_COLORS: Record<string, string> = {
  Soprasuolo: '#e74c3c', Sotterraneo: '#3498db', Colonnina: '#f39c12', Altro: '#95a5a6',
};
const TILE_LAYERS = {
  Stradale: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  Umanitaria: 'https://tile-{s}.openstreetmap.fr/hot/{z}/{x}/{y}.png',
  Satellitare: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
  Topografica: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
};

const REGIONI = [
  'Emilia-Romagna', 'Abruzzo', 'Basilicata', 'Calabria', 'Campania',
  'Friuli Venezia Giulia', 'Lazio', 'Liguria', 'Lombardia', 'Marche',
  'Molise', 'Piemonte', 'Puglia', 'Sardegna', 'Sicilia', 'Toscana',
  'Trentino-Alto Adige', 'Umbria', "Valle d'Aosta", 'Veneto',
];

// Hydrant interface estratta in ./types per condivisione con HydrantSchedaModal.

// GeoJSON Italian regions per filtro point-in-polygon (Bryan: posizione idrante
// in base a regione selezionata deve usare il file geojson)
type GeoJsonGeometry =
  | { type: 'Polygon'; coordinates: number[][][] }
  | { type: 'MultiPolygon'; coordinates: number[][][][] };
interface GeoFeature { type: 'Feature'; properties: Record<string, unknown>; geometry: GeoJsonGeometry }
interface GeoCollection { type: 'FeatureCollection'; features: GeoFeature[] }

// Algoritmo raycast: punto nel poligono se l'intersezione di una semiretta orizzontale
// con i lati del poligono e' dispari (1 = dentro, 0/2 = fuori)
function pointInRing(lng: number, lat: number, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersect = yi > lat !== yj > lat &&
      lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}
function pointInGeometry(lng: number, lat: number, geom: GeoJsonGeometry): boolean {
  if (geom.type === 'Polygon') {
    if (!pointInRing(lng, lat, geom.coordinates[0])) return false;
    for (let i = 1; i < geom.coordinates.length; i++) {
      if (pointInRing(lng, lat, geom.coordinates[i])) return false; // dentro a un foro
    }
    return true;
  }
  for (const poly of geom.coordinates) {
    if (!pointInRing(lng, lat, poly[0])) continue;
    let inHole = false;
    for (let i = 1; i < poly.length; i++) {
      if (pointInRing(lng, lat, poly[i])) { inHole = true; break; }
    }
    if (!inHole) return true;
  }
  return false;
}

// Normalizza i nomi regione (gli HTML/geojson usano varianti con/senza apostrofi e accenti)
function normalizeRegion(s: string): string {
  return s.toLowerCase()
    .replace(/[''']/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

interface HydrantsPageProps { publicView?: boolean }

export function HydrantsPage({ publicView }: HydrantsPageProps = {}): React.JSX.Element {
  usePageTitle(publicView ? 'Mappa Idranti' : 'Idranti');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const { user, hasRole } = useAuth();
  const isLogged = !!user;
  const isTecnico = hasRole('TecnicoIdranti', 'Amministratore', 'SuperAdmin');

  const [tile, setTile] = useState<keyof typeof TILE_LAYERS>('Stradale');
  const [addMode, setAddMode] = useState(false);
  const [region, setRegion] = useState<string>('Emilia-Romagna');
  const [editing, setEditing] = useState<Hydrant | null>(null);
  const [photoModal, setPhotoModal] = useState<string | null>(null);
  const [oldModalOpen, setOldModalOpen] = useState(false);
  const [schedaModal, setSchedaModal] = useState<Hydrant | null>(null);
  const [fabOpen, setFabOpen] = useState(false);
  const [fabPos, setFabPos] = useState(() => ({
    x: typeof window !== 'undefined' ? window.innerWidth - 80 : 0,
    y: typeof window !== 'undefined' ? window.innerHeight - 100 : 0,
  }));
  const [navDestination, setNavDestination] = useState<{ lat: number; lng: number } | null>(null);
  const [focusOnHydrant, setFocusOnHydrant] = useState<{ lat: number; lng: number } | null>(null);
  const [routeCoords, setRouteCoords] = useState<[number, number][] | null>(null);
  const [routeInfo, setRouteInfo] = useState<{ km: number; min: number } | null>(null);
  const [routeUserPos, setRouteUserPos] = useState<{ lat: number; lng: number } | null>(null);
  const [navLoading, setNavLoading] = useState(false);
  // Posizione attuale dell'utente: usata per il marker "Tu sei qui" sulla mappa.
  const [userPosition, setUserPosition] = useState<{ lat: number; lng: number } | null>(null);
  // Punto asse (Bryan): origine manuale dei percorsi. Se impostato sostituisce il
  // GPS come partenza per "idrante più comodo" e per il routing su un idrante; il
  // pallino "tu sei qui" viene nascosto e resta solo il mirino. axisMode = in
  // attesa del tap sulla mappa per posizionarlo.
  const [axisPoint, setAxisPoint] = useState<{ lat: number; lng: number } | null>(null);
  const [axisMode, setAxisMode] = useState(false);

  const { data: hydrants } = useQuery<Hydrant[]>({
    queryKey: ['hydrants'],
    queryFn: async () => (await api.get('/hydrants')).data,
  });

  // GeoJSON regioni italiane: cache permanente (file statico, ~600KB)
  const { data: regionsGeo } = useQuery<GeoCollection>({
    queryKey: ['italy-regions-geojson'],
    queryFn: async () => {
      const res = await fetch('https://raw.githubusercontent.com/openpolis/geojson-italy/master/geojson/limits_IT_regions.geojson');
      return res.json();
    },
    staleTime: Infinity, gcTime: Infinity,
  });

  // Feature della regione attualmente selezionata (per filtro point-in-polygon)
  const selectedRegionFeature = useMemo<GeoFeature | null>(() => {
    if (!regionsGeo) return null;
    const target = normalizeRegion(region);
    return regionsGeo.features.find(f => {
      const name = (f.properties?.reg_name ?? f.properties?.name ?? '') as string;
      return normalizeRegion(name) === target;
    }) ?? null;
  }, [regionsGeo, region]);

  // Filtro idranti per regione: se geojson disponibile usa point-in-polygon su lat/lng,
  // fallback al campo region (default Emilia-Romagna per dati pre-migration)
  const filteredHydrants = useMemo(() => {
    const all = hydrants ?? [];
    if (selectedRegionFeature) {
      return all.filter(h => pointInGeometry(h.longitude, h.latitude, selectedRegionFeature.geometry));
    }
    return all.filter(h => (h.region ?? 'Emilia-Romagna') === region);
  }, [hydrants, selectedRegionFeature, region]);

  const add = useMutation({
    mutationFn: async (req: { latitude: number; longitude: number; type: string; region: string }) => {
      await api.post('/hydrants', req);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hydrants'] }),
  });

  const del = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/hydrants/${id}`); },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hydrants'] });
      toast.show('Idrante eliminato', 'success');
    },
  });

  async function handleDelete(h: Hydrant): Promise<void> {
    const ok = await confirm({
      title: 'Elimina idrante',
      message: `Eliminare l'idrante${h.code ? ` "${h.code}"` : ''}?`,
      confirmLabel: '🗑️ Elimina', variant: 'danger', icon: '🗑️',
    });
    if (ok) del.mutate(h.id);
  }

  function ClickHandler(): null {
    useMapEvents({
      click: (e) => {
        // Punto asse: un solo tap posiziona (o riposiziona) il mirino e chiude
        // la modalità. Prevale su addMode (che è per gli idranti).
        if (axisMode) {
          setAxisPoint({ lat: e.latlng.lat, lng: e.latlng.lng });
          setAxisMode(false);
          return;
        }
        if (addMode && !publicView) {
          // Region calcolata da geojson lato client (point-in-polygon su lat/lng cliccate);
          // fallback al dropdown se geojson non ancora caricato.
          let detectedRegion = region;
          if (regionsGeo) {
            const match = regionsGeo.features.find(f =>
              pointInGeometry(e.latlng.lng, e.latlng.lat, f.geometry));
            if (match) {
              detectedRegion = ((match.properties?.reg_name ?? match.properties?.name) as string) ?? region;
            }
          }
          add.mutate({ latitude: e.latlng.lat, longitude: e.latlng.lng, type: 'Soprasuolo', region: detectedRegion });
          setAddMode(false);
        }
      },
    });
    return null;
  }

  // Banner ricerca: feedback visivo per "Centra mappa" / "Idrante piu' comodo"
  // mentre cerchiamo la posizione GPS o calcoliamo la route. Auto-hide al
  // completamento o errore.
  const [searchingBanner, setSearchingBanner] = useState<string | null>(null);

  // All'avvio acquisisce silenziosamente la posizione utente per mostrare
  // il marker "Tu sei qui" sulla mappa. Niente prompt: se permesso negato,
  // nessun marker (l'utente puo' fare Centra Mappa esplicitamente).
  useEffect(() => {
    if (!navigator.geolocation) return;
    const watchId = navigator.geolocation.watchPosition(
      p => setUserPosition({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => { /* silenzioso */ },
      { enableHighAccuracy: false, maximumAge: 30_000, timeout: 15_000 },
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  async function ensureGpsThen<T>(action: () => Promise<T>, label: string): Promise<T | null> {
    setSearchingBanner(label);
    try {
      // Pre-check stato GPS sul wrapper nativo. Se off, prompt + apertura settings.
      const { checkNativeGpsEnabled, openNativeGpsSettings, isInNativeWrapper } =
        await import('@/services/nativeBridge');
      if (isInNativeWrapper()) {
        const enabled = await checkNativeGpsEnabled();
        if (!enabled) {
          const ok = window.confirm(
            'Il GPS del telefono e\' spento.\n\n'
            + 'Per usare questa funzione e\' necessario accenderlo.\n\n'
            + 'Apro le impostazioni GPS?',
          );
          if (ok) openNativeGpsSettings();
          setSearchingBanner(null);
          return null;
        }
      }
      return await action();
    } finally {
      setSearchingBanner(null);
    }
  }

  function centerMap(): void {
    if (!navigator.geolocation) {
      toast.show('Geolocalizzazione non disponibile', 'error');
      return;
    }
    void ensureGpsThen(() => new Promise<void>(resolve => {
      navigator.geolocation.getCurrentPosition(
        p => {
          setUserPosition({ lat: p.coords.latitude, lng: p.coords.longitude });
          setFocusOnHydrant({ lat: p.coords.latitude, lng: p.coords.longitude });
          resolve();
        },
        () => { toast.show('Impossibile ottenere la posizione', 'error'); resolve(); },
        { timeout: 10000, enableHighAccuracy: true },
      );
    }), '📍 Ricerca posizione in corso...');
  }

  // Origine dei percorsi: se è stato piazzato un "punto asse" (Bryan) usiamo
  // quello, altrimenti il GPS (con il pre-check nativo via ensureGpsThen).
  // Ritorna null se la posizione non è disponibile/negata.
  async function resolveOrigin(label: string): Promise<{ lat: number; lng: number } | null> {
    if (axisPoint) return axisPoint;
    return ensureGpsThen(() => new Promise<{ lat: number; lng: number } | null>(resolve => {
      if (!navigator.geolocation) { resolve(null); return; }
      navigator.geolocation.getCurrentPosition(
        p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => resolve(null),
        { timeout: 10000, enableHighAccuracy: true },
      );
    }), label);
  }

  async function findBestHydrant(): Promise<void> {
    const origin = await resolveOrigin('📍 Ricerca posizione in corso...');
    if (!origin) {
      toast.show('Impossibile ottenere la posizione', 'error');
      return;
    }
    // Col GPS aggiorniamo anche il marker "tu sei qui"; col punto asse no
    // (viene mostrato solo il mirino).
    if (!axisPoint) setUserPosition(origin);
    setSearchingBanner('🔎 Calcolo percorso al più vicino...');
    try {
      const res = await api.get('/hydrants/nearest', {
        params: { lat: origin.lat, lng: origin.lng, take: 1 },
      });
      const best = res.data?.[0]?.hydrant;
      const km = res.data?.[0]?.distanceKm;
      if (best) {
        toast.show(`Idrante più comodo: ${best.code ?? 'senza codice'} (${km?.toFixed(1)} km)`, 'success');
        await startInAppNavigation(best.latitude, best.longitude);
      } else {
        toast.show('Nessun idrante trovato nel database', 'error');
      }
    } catch {
      toast.show('Errore durante la ricerca dell\'idrante', 'error');
    } finally {
      setSearchingBanner(null);
    }
  }

  function exportExcel(): void {
    // Punto 6: prima usciva in silenzio se non c'erano idranti -> sembrava "non
    // fa nulla". Ora feedback esplicito + try/catch per scoprire errori runtime.
    if (!filteredHydrants.length) {
      toast.show(`Nessun idrante da esportare per "${region}"`, 'error');
      return;
    }
    try {
      downloadXlsx(filteredHydrants.map(h => ({
        Codice: h.code ?? '', Tipo: h.type, Regione: h.region ?? '',
        Comune: h.municipality ?? '', Via: h.street ?? '',
        Lat: h.latitude, Lng: h.longitude,
        'Pressione (bar)': h.pressureBar ?? '', 'Diametro (mm)': h.diameterMm ?? '',
        'DN UNI': h.uniDiameter ?? '', Risorsa: h.resource ?? '',
        Efficiente: h.efficient === true ? 'SI' : h.efficient === false ? 'NO' : '',
        'Ultima ispezione': h.lastInspectionAt ? new Date(h.lastInspectionAt).toLocaleDateString('it-IT') : '',
        Note: h.notes ?? '',
      })), 'Idranti', `idranti_${region}_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.show(`Esportati ${filteredHydrants.length} idranti`);
    } catch (err) {
      console.error('Export Excel idranti fallito:', err);
      toast.show('Errore export Excel', 'error');
    }
  }

  function openGoogleMaps(lat: number, lng: number): void {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
  }

  // Navigazione in-app via OSRM (open-source). Disegna il percorso reale sulla mappa
  // dalla posizione utente all'idrante. NIENTE Google Maps automatico (richiesta Bryan).
  async function startInAppNavigation(destLat: number, destLng: number): Promise<void> {
    const origin = await resolveOrigin('📍 Ricerca posizione in corso...');
    if (!origin) {
      toast.show('Posizione GPS non disponibile', 'error');
      return;
    }
    setNavLoading(true);
    try {
      const from = `${origin.lng},${origin.lat}`;
      const to = `${destLng},${destLat}`;
      const url = `https://router.project-osrm.org/route/v1/driving/${from};${to}?overview=full&geometries=geojson`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.code !== 'Ok' || !data.routes?.[0]) {
        toast.show('Impossibile calcolare il percorso', 'error');
        setNavLoading(false);
        return;
      }
      const route = data.routes[0];
      // OSRM ritorna [lng, lat]; Leaflet vuole [lat, lng]
      const coords: [number, number][] = route.geometry.coordinates.map((c: [number, number]) => [c[1], c[0]]);
      setRouteCoords(coords);
      setRouteInfo({ km: route.distance / 1000, min: route.duration / 60 });
      // Marker origine: col punto asse mostriamo il mirino (nel render), quindi
      // non impostiamo routeUserPos (che è il pallino GPS "tu sei qui").
      setRouteUserPos(axisPoint ? null : origin);
      setNavDestination({ lat: destLat, lng: destLng });
      toast.show(`Percorso: ${(route.distance / 1000).toFixed(1)} km · ${Math.round(route.duration / 60)} min`, 'success');
    } catch {
      toast.show('Errore nel calcolo del percorso', 'error');
    }
    setNavLoading(false);
  }

  function stopNavigation(): void {
    setRouteCoords(null);
    setRouteInfo(null);
    setRouteUserPos(null);
    setNavDestination(null);
  }

  function speakDirections(lat: number, lng: number): void {
    if (!('speechSynthesis' in window)) {
      toast.show('Sintesi vocale non disponibile', 'error');
      return;
    }
    navigator.geolocation.getCurrentPosition(p => {
      const dist = haversineM(p.coords.latitude, p.coords.longitude, lat, lng);
      const km = (dist / 1000).toFixed(1);
      const msg = new SpeechSynthesisUtterance(`Idrante a ${km} chilometri. Apri Google Maps per la navigazione vocale completa.`);
      msg.lang = 'it-IT';
      window.speechSynthesis.speak(msg);
    });
  }

  // "Da controllare": ultima ispezione > 1 anno fa o mai
  const toCheck = filteredHydrants.filter(h => {
    if (!h.lastInspectionAt && !h.createdAt) return false;
    const checkDate = h.lastInspectionAt ?? h.createdAt!;
    const daysAgo = (Date.now() - new Date(checkDate).getTime()) / 86400000;
    return daysAgo > 365;
  });

  const mapCenter: [number, number] = [44.327, 10.594];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100%', overflow: 'hidden' }}>
      {/* Toolbar superiore (NON overlay sulla mappa) */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '8px 12px', background: '#fff', borderBottom: '1px solid #dee2e6',
        boxShadow: '0 2px 4px rgba(0,0,0,0.06)', flexShrink: 0, color: '#333',
      }}>
        {/* Punto 5: Indietro + Regione SEMPRE sulla stessa riga (nowrap); font
            adattivo (clamp) e select che si stringe (minWidth:0) per restare su
            una riga anche su schermi piccoli. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap', minWidth: 0 }}>
          <button
            onClick={() => navigate(isLogged ? '/dashboard' : '/login')}
            style={{
              background: '#dc3545', color: 'white', border: 'none', borderRadius: 6,
              padding: '8px 12px', cursor: 'pointer', fontWeight: 700,
              fontSize: 'clamp(12px, 3vw, 14px)', whiteSpace: 'nowrap', flexShrink: 0,
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
            ← Indietro
          </button>
          <label htmlFor="regione" style={{ fontSize: 'clamp(10px, 2.6vw, 12px)', color: '#666', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}>📍 Regione:</label>
          <select id="regione" value={region} onChange={e => setRegion(e.target.value)}
            style={{ ...toolbarSelect, minWidth: 0, flexShrink: 1, fontSize: 'clamp(11px, 2.8vw, 14px)' }}>
            {REGIONI.map(r => <option key={r} value={r} style={toolbarOption}>{r}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="tile" style={{ fontSize: 12, color: '#666', fontWeight: 700, marginRight: 6 }}>🗺️ Mappa:</label>
          <select id="tile" value={tile} onChange={e => setTile(e.target.value as keyof typeof TILE_LAYERS)} style={toolbarSelect}>
            {(Object.keys(TILE_LAYERS) as Array<keyof typeof TILE_LAYERS>).map(t => (
              <option key={t} value={t} style={toolbarOption}>{t}</option>
            ))}
          </select>
        </div>
        <div style={{ marginLeft: 'auto', fontSize: 12, color: '#666' }}>
          Idranti visualizzati: <strong>{filteredHydrants.length}</strong>
        </div>
      </div>

      {/* Riga legenda — punto 5: SEMPRE su una sola riga (nowrap) con font e gap
          adattivi (clamp) per stare su una riga a qualsiasi larghezza schermo. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 'clamp(6px, 2vw, 16px)', flexWrap: 'nowrap',
        whiteSpace: 'nowrap', overflow: 'hidden',
        padding: '6px 12px', background: '#f8f9fa', borderBottom: '1px solid #e9ecef',
        fontSize: 'clamp(9px, 2.4vw, 12px)', color: '#333', flexShrink: 0,
      }}>
        <strong style={{ color: '#666' }}>Legenda:</strong>
        <span><span style={{ color: HYDRANT_COLORS.Soprasuolo, fontSize: '1.3em' }}>●</span> Soprasuolo</span>
        <span><span style={{ color: HYDRANT_COLORS.Sotterraneo, fontSize: '1.3em' }}>●</span> Sotterraneo</span>
        <span><span style={{ color: HYDRANT_COLORS.Colonnina, fontSize: '1.3em' }}>●</span> Colonnina</span>
        <span><span style={{ color: HYDRANT_COLORS.Altro, fontSize: '1.3em' }}>●</span> Altro</span>
      </div>

      {/* Area mappa (occupa lo spazio restante) */}
      <div style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>

      {addMode && (
        <div style={{
          position: 'absolute', top: 70, left: 12, zIndex: 1000,
          background: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: 6,
          padding: 10, fontSize: 13, boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
        }}>
          📍 <strong>Modalità Aggiungi:</strong> Clicca sulla mappa
          <button onClick={() => setAddMode(false)} style={{ marginLeft: 10, padding: '4px 10px',
            border: 'none', borderRadius: 4, background: '#dc3545', color: 'white', cursor: 'pointer' }}>
            Annulla
          </button>
        </div>
      )}

      {axisMode && (
        <div style={{
          position: 'absolute', top: 70, left: 12, zIndex: 1000,
          background: '#e7f3ff', border: '1px solid #b6d8ff', borderRadius: 6,
          padding: 10, fontSize: 13, boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
        }}>
          🎯 <strong>Punto asse:</strong> tocca la mappa per posizionarlo
          <button onClick={() => setAxisMode(false)} style={{ marginLeft: 10, padding: '4px 10px',
            border: 'none', borderRadius: 4, background: '#dc3545', color: 'white', cursor: 'pointer' }}>
            Annulla
          </button>
        </div>
      )}

      {/* Banner ricerca overlay (centra mappa / idrante piu' comodo / route).
          Stilo come banner fluttuante centrale, sopra la mappa. */}
      {searchingBanner && (
        <div style={{
          position: 'fixed', top: 80, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.8)', color: 'white', padding: '10px 18px',
          borderRadius: 24, fontSize: 14, fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 10, zIndex: 1500,
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        }}>
          <span className="upload-spinner" style={{ borderTopColor: 'white' }} />
          {searchingBanner}
        </div>
      )}

      {/* Mappa fullscreen */}
      <MapContainer center={mapCenter} zoom={11} style={{ height: '100%', width: '100%' }}>
        <TileLayer url={TILE_LAYERS[tile]} attribution="&copy; OpenStreetMap contributors" />
        <ClickHandler />
        <FocusController target={focusOnHydrant} />
        <RouteFitter coords={routeCoords} />

        {/* Polyline percorso OSRM + marker posizione utente */}
        {routeCoords && (
          <Polyline positions={routeCoords} pathOptions={{ color: '#dc3545', weight: 5, opacity: 0.85 }} />
        )}
        {/* Punto asse (Bryan): quando presente si mostra SOLO il mirino (niente
            "tu sei qui"): è l'origine di "idrante più comodo" e del routing. Tap
            sul mirino -> Popup con Elimina. Altrimenti marker posizione utente
            (o origine percorso durante una navigazione GPS). */}
        {axisPoint ? (
          <Marker position={[axisPoint.lat, axisPoint.lng]}
            icon={L.divIcon({
              className: '',
              html: '<div style="width:28px;height:28px;position:relative">'
                + '<div style="position:absolute;inset:2px;border:3px solid #dc3545;border-radius:50%;background:rgba(220,53,69,0.12);box-shadow:0 0 6px rgba(220,53,69,0.8)"></div>'
                + '<div style="position:absolute;left:12px;top:-3px;width:4px;height:34px;background:#dc3545;border-radius:2px"></div>'
                + '<div style="position:absolute;top:12px;left:-3px;height:4px;width:34px;background:#dc3545;border-radius:2px"></div>'
                + '</div>',
              iconSize: [28, 28], iconAnchor: [14, 14],
            })}>
            <Popup>
              <div style={{ minWidth: 170 }}>
                <strong>🎯 Punto asse</strong>
                <div style={{ fontSize: 12, color: '#666', margin: '4px 0 8px' }}>
                  Origine dei percorsi (al posto del GPS).
                </div>
                <button onClick={() => setAxisPoint(null)} style={popupBtn('#dc3545')}>🗑️ Elimina</button>
              </div>
            </Popup>
          </Marker>
        ) : (routeUserPos ?? userPosition) && (
          <Marker position={[(routeUserPos ?? userPosition)!.lat, (routeUserPos ?? userPosition)!.lng]}
            icon={L.divIcon({
              className: '',
              html: '<div style="width:20px;height:20px;border-radius:50%;background:#007bff;border:3px solid white;box-shadow:0 0 8px rgba(0,123,255,0.9)"></div>',
              iconSize: [20, 20], iconAnchor: [10, 10],
            })}>
            <Popup>📍 La tua posizione</Popup>
          </Marker>
        )}

        {filteredHydrants.map(h => {
          // Punto 7: solo foto nel sistema attuale; le legacy (vecchio host) escluse.
          const photo = currentPhotoSrc(h.photoPath);
          return (
          <Marker key={h.id} position={[h.latitude, h.longitude]}
            icon={L.divIcon({
              className: '',
              html: `<div style="width:18px;height:18px;border-radius:50%;background:${HYDRANT_COLORS[h.type] ?? '#666'};border:2px solid white;box-shadow:0 0 4px rgba(0,0,0,0.5)"></div>`,
              iconSize: [18, 18], iconAnchor: [9, 9],
            })}>
            <Popup>
              <div style={{ minWidth: 220 }}>
                {photo && (
                  <img src={photo} alt="" onClick={() => setPhotoModal(photo)}
                    style={{ width: '100%', maxHeight: 150, objectFit: 'cover', borderRadius: 6,
                             marginBottom: 8, cursor: 'pointer' }} />
                )}
                <div><strong>Tipo:</strong> {h.type}</div>
                {h.code && <div><strong>Codice:</strong> {h.code}</div>}
                <div><strong>Ultimo controllo:</strong>{' '}
                  {h.lastInspectionAt ? new Date(h.lastInspectionAt).toLocaleDateString('it-IT')
                    : h.createdAt ? new Date(h.createdAt).toLocaleDateString('it-IT') : '—'}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 10 }}>
                  <button onClick={() => startInAppNavigation(h.latitude, h.longitude)}
                    disabled={navLoading} style={popupBtn('#28a745')}>
                    🧭 {navLoading ? 'Calcolo percorso...' : 'Indicazioni Stradali'}
                  </button>
                  <button onClick={() => setSchedaModal(h)} style={popupBtn('#007bff')}>✏️ Scheda</button>
                  {photo && (
                    <button onClick={() => setPhotoModal(photo)} style={popupBtn('#17a2b8')}>📷 Visualizza Foto</button>
                  )}
                  {isTecnico && !publicView && (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => setEditing(h)} style={{ ...popupBtn('#007bff'), flex: 1 }}>✏️ Modifica</button>
                      <button onClick={() => handleDelete(h)} style={{ ...popupBtn('#dc3545'), flex: 1 }}>🗑️ Elimina</button>
                    </div>
                  )}
                </div>
              </div>
            </Popup>
          </Marker>
          );
        })}
      </MapContainer>

      {/* Banner navigazione attiva con bottone Termina (Bryan: pulsante rosso in basso centro) */}
      {routeCoords && (
        <div style={{
          position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
          zIndex: 1100, background: 'white', borderRadius: 12, padding: '10px 16px',
          boxShadow: '0 6px 20px rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', gap: 14,
        }}>
          {routeInfo && (
            <div style={{ fontSize: 13, color: '#333' }}>
              <strong>🧭 Navigazione attiva</strong><br />
              {routeInfo.km.toFixed(1)} km · {Math.round(routeInfo.min)} min
            </div>
          )}
          <button onClick={stopNavigation}
            style={{
              background: '#dc3545', color: 'white', border: 'none', borderRadius: 8,
              padding: '10px 18px', cursor: 'pointer', fontWeight: 700, fontSize: 14,
            }}>
            ✖ Termina navigazione
          </button>
        </div>
      )}
      </div>{/* fine area mappa */}

      {/* FAB Volante draggable con menu — visibile sempre (anche pubblicView),
          ma alcune funzioni avanzate solo per utenti loggati con ruolo */}
      <FabMenu
        pos={fabPos} onMove={setFabPos}
        open={fabOpen} onToggle={() => setFabOpen(o => !o)}
        isTecnico={isTecnico && !publicView}
        navActive={!!navDestination}
        onAddHydrant={() => { setAddMode(true); setFabOpen(false); }}
        onCenterMap={() => { centerMap(); setFabOpen(false); }}
        onBest={() => { findBestHydrant(); setFabOpen(false); }}
        axisActive={!!axisPoint}
        onAxisPoint={() => { setAxisMode(true); setFabOpen(false); }}
        onToCheck={() => { setOldModalOpen(true); setFabOpen(false); }}
        onExportExcel={() => { exportExcel(); setFabOpen(false); }}
        onGuidami={() => navDestination && speakDirections(navDestination.lat, navDestination.lng)}
        onGoogleMaps={() => navDestination && openGoogleMaps(navDestination.lat, navDestination.lng)}
      />

      {/* Modali */}
      {editing && <HydrantEditModal hydrant={editing} onClose={() => setEditing(null)} />}
      {schedaModal && <HydrantSchedaModal hydrant={schedaModal} onClose={() => setSchedaModal(null)} />}
      {photoModal && (
        <div className="modal-overlay" onClick={() => setPhotoModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '90vw', padding: 8 }}>
            <img src={photoModal} alt="" style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
          </div>
        </div>
      )}
      {oldModalOpen && (
        <OldHydrantsModal hydrants={toCheck} onClose={() => setOldModalOpen(false)}
          onSelect={h => { setOldModalOpen(false); setFocusOnHydrant({ lat: h.latitude, lng: h.longitude }); }} />
      )}
    </div>
  );
}

// colorScheme: 'light' forza il rendering chiaro degli <option> anche se l'OS
// e' in dark mode (Windows/macOS) — risolve "testo bianco su sfondo bianco".
const toolbarSelect: React.CSSProperties = {
  padding: '6px 10px', borderRadius: 6, border: '1px solid #ced4da', fontSize: 14,
  color: '#333', background: 'white', colorScheme: 'light',
};
const toolbarOption: React.CSSProperties = { color: '#333', background: 'white' };

function popupBtn(bg: string): React.CSSProperties {
  return {
    padding: '8px 10px', border: 'none', cursor: 'pointer',
    background: bg, color: 'white', borderRadius: 6, fontWeight: 700, fontSize: 13,
  };
}

function FocusController({ target }: { target: { lat: number; lng: number } | null }): null {
  const map = useMap();
  useEffect(() => {
    if (target) map.setView([target.lat, target.lng], 16);
  }, [target, map]);
  return null;
}

// Centra/zooma la mappa per inquadrare l'intero percorso OSRM quando viene calcolato
function RouteFitter({ coords }: { coords: [number, number][] | null }): null {
  const map = useMap();
  useEffect(() => {
    if (coords && coords.length > 1) {
      const bounds = L.latLngBounds(coords.map(c => L.latLng(c[0], c[1])));
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [coords, map]);
  return null;
}

interface FabMenuProps {
  pos: { x: number; y: number };
  onMove: (p: { x: number; y: number }) => void;
  open: boolean;
  onToggle: () => void;
  isTecnico: boolean;
  navActive: boolean;
  axisActive: boolean;
  onAddHydrant: () => void;
  onCenterMap: () => void;
  onBest: () => void;
  onAxisPoint: () => void;
  onToCheck: () => void;
  onExportExcel: () => void;
  onGuidami: () => void;
  onGoogleMaps: () => void;
}

function FabMenu({ pos, onMove, open, onToggle, isTecnico, navActive, axisActive,
  onAddHydrant, onCenterMap, onBest, onAxisPoint, onToCheck, onExportExcel, onGuidami, onGoogleMaps }: FabMenuProps): React.JSX.Element {
  // pressed: vero solo tra pointerdown e pointerup (evita drag su hover semplice)
  const pressedRef = useRef(false);
  const draggingRef = useRef(false);
  const startRef = useRef({ x: 0, y: 0, time: 0 });

  function onPointerDown(e: React.PointerEvent<HTMLButtonElement>): void {
    pressedRef.current = true;
    draggingRef.current = false;
    startRef.current = { x: e.clientX - pos.x, y: e.clientY - pos.y, time: Date.now() };
    // Cattura il puntatore: tutti i pointermove successivi vanno SOLO a questo elemento
    // finche' non rilasciamo, anche se l'utente esce dal bottone.
    e.currentTarget.setPointerCapture(e.pointerId);
  }
  function onPointerMove(e: React.PointerEvent<HTMLButtonElement>): void {
    if (!pressedRef.current) return; // hover senza click = ignoro
    if (Math.abs(e.clientX - startRef.current.x - pos.x) > 5 || Math.abs(e.clientY - startRef.current.y - pos.y) > 5) {
      draggingRef.current = true;
      onMove({ x: e.clientX - startRef.current.x, y: e.clientY - startRef.current.y });
    }
  }
  function onPointerUp(e: React.PointerEvent<HTMLButtonElement>): void {
    if (pressedRef.current && e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    pressedRef.current = false;
    if (!draggingRef.current && Date.now() - startRef.current.time < 300) onToggle();
  }
  function onPointerCancel(): void {
    pressedRef.current = false;
    draggingRef.current = false;
  }

  return (
    <div className="controls-wrapper" style={{ position: 'fixed', left: pos.x, top: pos.y, zIndex: 9998 }}>
      <button id="mainMenuBtn" className="main-fab"
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        type="button"
        style={{
          width: 60, height: 60, borderRadius: '50%',
          background: open
            ? 'linear-gradient(135deg, #dc3545 0%, #a71d2a 100%)'
            : 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
          color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 26, cursor: 'move', userSelect: 'none', touchAction: 'none',
          border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
          transition: 'background 0.2s ease',
        }}>{open ? '✕' : '☰'}</button>

      {open && (
        <div className="controls-panel" id="controlsPanel" style={{
          position: 'absolute', bottom: 70, right: 0,
          background: 'white', borderRadius: 14, padding: 12,
          boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
          display: 'flex', flexDirection: 'column', gap: 8, minWidth: 220,
        }}>
          {isTecnico && (
            <button id="addHydrantBtn" onClick={onAddHydrant} style={fabMenuBtn('#28a745')}>📍 Aggiungi Idrante</button>
          )}
          <button onClick={onCenterMap} style={fabMenuBtn('#17a2b8')}>🎯 Centra Mappa</button>
          <button id="findBestBtn" onClick={onBest} style={fabMenuBtn('#007bff')}>🚰 Idrante più comodo</button>
          <button id="axisPointBtn" onClick={onAxisPoint} style={fabMenuBtn('#6f42c1')}>
            🎯 {axisActive ? 'Sposta punto asse' : 'Punto asse'}
          </button>
          {isTecnico && (
            <button id="checkOldHydrantsBtn" onClick={onToCheck} style={fabMenuBtn('#ff9800')}>⚠️ Idranti da Controllare</button>
          )}
          {isTecnico && (
            <button id="exportexcel" onClick={onExportExcel} style={fabMenuBtn('#fd7e14')}>📄 Esporta Excel</button>
          )}
          {navActive && (
            <>
              <button id="guidamiBtn" onClick={onGuidami} style={fabMenuBtn('#9c27b0')}>🔊 Guidami</button>
              <button id="linkBtn" onClick={onGoogleMaps} style={fabMenuBtn('#007bff')}>🗺️ Google Maps</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Punto 7: mostra SOLO le foto nel sistema attuale (path relativo /api/...).
// Le foto legacy che puntano al vecchio host (URL assoluto http/https, es.
// Firebase Storage) NON vengono mostrate: vanno ricaricate nel sistema attuale.
function currentPhotoSrc(photoPath?: string | null): string | undefined {
  if (!photoPath) return undefined;
  if (/^https?:\/\//i.test(photoPath)) return undefined; // vecchio host -> escluso
  return photoPath;
}

function fabMenuBtn(bg: string): React.CSSProperties {
  return {
    padding: '10px 14px', border: 'none', borderRadius: 6, cursor: 'pointer',
    background: bg, color: 'white', fontWeight: 600, fontSize: 14, textAlign: 'left',
  };
}

function OldHydrantsModal({ hydrants, onClose, onSelect }: {
  hydrants: Hydrant[]; onClose: () => void; onSelect: (h: Hydrant) => void;
}): React.JSX.Element {
  function age(h: Hydrant): { days: number; cls: 'warn' | 'crit' } {
    const date = h.lastInspectionAt ?? h.createdAt;
    if (!date) return { days: 9999, cls: 'crit' };
    const days = Math.floor((Date.now() - new Date(date).getTime()) / 86400000);
    return { days, cls: days < 730 ? 'warn' : 'crit' };
  }
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 560, maxHeight: '80vh', overflowY: 'auto' }}>
        <h3>⚠️ Idranti da controllare ({hydrants.length})</h3>
        <p style={{ fontSize: 12, color: '#666' }}>
          Verde: ultimo controllo &lt; 1 anno · Giallo: &gt; 1 anno · Rosso: mai/&gt; 2 anni. Clicca per centrarli sulla mappa.
        </p>
        {hydrants.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#888', padding: 30 }}>Tutti gli idranti sono aggiornati</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {hydrants.map(h => {
              const a = age(h);
              const border = a.cls === 'crit' ? '#dc3545' : '#ffc107';
              return (
                <button key={h.id} onClick={() => onSelect(h)}
                  style={{ padding: 10, border: `2px solid ${border}`, borderRadius: 6,
                           background: '#fff', cursor: 'pointer', textAlign: 'left',
                           display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>{h.code ?? 'Senza codice'}</strong> · {h.type}
                    <div style={{ fontSize: 12, color: '#666' }}>{h.municipality ?? ''} {h.street ?? ''}</div>
                  </div>
                  <div style={{ fontSize: 12, color: border, fontWeight: 700 }}>
                    {h.lastInspectionAt ? `${a.days}gg fa` : 'Mai controllato'}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Scheda Idrante completa — 17 campi richiesti dal cliente VVF Frassinoro
// (Bryan: "vecchia guardia che vuole tutto in cartaceo"):
// 1. codice idrante, 2. comune, 3. via, 4. civico, 5. data ultimo controllo (auto),
// 6. compilatore, 7. tipo idrante, 8. UNI (diametro), 9. risorsa (pubblica/privata),
// 10. lat/lng (read-only), 11. stato efficienza, 12. stato visivo, 13. valvola chiusura,
// 14. cartello segnaletico, 15. pressione, 16. accessibilità mezzi pesanti APS/ABP min 2,5mt
// + note accessibilità, 17. fotografia (eseguita/non eseguita) + note generali.
// Tutti gli altri 17 campi si compilano dalla Scheda.
function HydrantEditModal({ hydrant, onClose }: { hydrant: Hydrant; onClose: () => void }): React.JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    type: hydrant.type ?? 'Soprasuolo',
    pressureBar: hydrant.pressureBar?.toString() ?? '',
    diameterMm: hydrant.diameterMm?.toString() ?? '',
    notes: hydrant.notes ?? '',
  });
  async function uploadPhoto(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/hydrants/${hydrant.id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      qc.invalidateQueries({ queryKey: ['hydrants'] });
      toast.show('Foto caricata', 'success');
    } catch {
      toast.show('Errore upload foto', 'error');
    }
    setUploading(false);
    e.target.value = '';
  }
  const save = useMutation({
    mutationFn: async () => {
      await api.put(`/hydrants/${hydrant.id}`, {
        type: form.type,
        latitude: hydrant.latitude, longitude: hydrant.longitude,
        region: hydrant.region ?? null,
        pressureBar: form.pressureBar ? parseFloat(form.pressureBar) : null,
        diameterMm: form.diameterMm ? parseFloat(form.diameterMm) : null,
        notes: form.notes || null,
      });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['hydrants'] }); toast.show('Idrante aggiornato', 'success'); onClose(); },
    onError: () => toast.show('Errore aggiornamento', 'error'),
  });
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <h3>💧 Modifica Idrante</h3>
        <p style={{ fontSize: 12, color: '#888', margin: '0 0 12px' }}>
          Dati base. Per la scheda completa (codice, comune, via, accessibilità, ecc.) usa il pulsante "Scheda".
        </p>
        <div style={{ display: 'grid', gap: 10 }}>
          <label>Tipo
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} style={{ width: '100%', padding: 8 }}>
              <option value="Soprasuolo">Soprasuolo</option>
              <option value="Sotterraneo">Sotterraneo</option>
              <option value="Colonnina">Colonnina</option>
              <option value="Altro">Altro</option>
            </select>
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <label>Pressione (bar)
              <input type="number" step="0.1" value={form.pressureBar}
                onChange={e => setForm({ ...form, pressureBar: e.target.value })} style={{ width: '100%', padding: 8 }} />
            </label>
            <label>Diametro (mm)
              <input type="number" value={form.diameterMm}
                onChange={e => setForm({ ...form, diameterMm: e.target.value })} style={{ width: '100%', padding: 8 }} />
            </label>
          </div>
          <label>Note
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
              style={{ width: '100%', padding: 8, minHeight: 60 }} />
          </label>
          <label>Foto idrante
            {hydrant.photoPath && (
              <img src={hydrant.photoPath} alt="" style={{ display: 'block', maxWidth: 200, maxHeight: 150, borderRadius: 6, marginBottom: 6 }} />
            )}
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadPhoto} disabled={uploading}
              style={{ width: '100%', padding: 8 }} />
            <small style={{ color: '#888', fontSize: 11 }}>JPG/PNG/WEBP, max 5MB</small>
          </label>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 14 }}>
          <button onClick={onClose} className="btn-secondary">Annulla</button>
          <button onClick={() => save.mutate()} disabled={save.isPending}
            style={{ padding: '8px 16px', border: 'none', borderRadius: 6, cursor: 'pointer',
                     background: '#28a745', color: 'white', fontWeight: 700 }}>
            💾 Salva
          </button>
        </div>
      </div>
    </div>
  );
}

function haversineM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const toRad = (d: number) => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
